import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { PROFILE_LANGUAGES, ProfileLanguage } from 'models/organization-profile';

type ProfileLanguageSelectProps = {
    value: ProfileLanguage;
    onChange: (language: ProfileLanguage) => void;
    /** Languages that already have text — marked with a check. */
    writtenLanguages: ProfileLanguage[];
    dataTest: string;
};

/**
 * DD-3662: picks which language's texts are being edited. Each language a text was
 * written in carries a check, so the lab sees at a glance what readers can get.
 */
const ProfileLanguageSelect: FC<ProfileLanguageSelectProps> = ({ value, onChange, writtenLanguages, dataTest }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const labelId = `${dataTest}-label`;
    return (
        <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id={labelId}>{translate('lab-settings:PROFILE_LANGUAGE')}</InputLabel>
            <Select
                labelId={labelId}
                label={translate('lab-settings:PROFILE_LANGUAGE')}
                value={value}
                onChange={(event) => onChange(event.target.value as ProfileLanguage)}
                data-test={dataTest}
            >
                {PROFILE_LANGUAGES.map((language) => (
                    <MenuItem key={language.code} value={language.code}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                            {language.label}
                            {writtenLanguages.includes(language.code) ? (
                                <CheckIcon
                                    fontSize="small"
                                    color="success"
                                    sx={{ ml: 2 }}
                                    aria-label={translate('lab-settings:PROFILE_LANGUAGE_WRITTEN')}
                                />
                            ) : undefined}
                        </Box>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default ProfileLanguageSelect;
